import { useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Card, Code, Group, Stack, Text } from "@mantine/core";
import { PageHeader } from "@/components/PageHeader";
import { DataTable } from "@/components/DataTable";
import { EmptyState } from "@/components/states";
import { useFirestoreDoc, useFirestoreQuery } from "@/firestore/hooks";
import type { WithDocId } from "@/firestore/types";
import { COLLECTIONS } from "@/firestore/collections";
import { columnsFor } from "@/entities/registry";
import { entityHasDetail, entityLabel, entityPath } from "@/routes/paths";
import type { EntityKey } from "@/routes/paths";

const RE_ADDRESS = /^0x[0-9a-fA-F]{40}$/;
const RE_UUID = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
const RE_LASTFOUR = /^\d{4}$/;
const RE_HANDLE = /^@?[a-z0-9_]{3,30}$/i;

interface Group {
  entity: EntityKey;
  rows: WithDocId<Record<string, unknown>>[];
}

function dedupe(rows: WithDocId<Record<string, unknown>>[]): WithDocId<Record<string, unknown>>[] {
  const seen = new Set<string>();
  return rows.filter((r) => (seen.has(r._docId) ? false : (seen.add(r._docId), true)));
}

export function SearchPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const q = (params.get("q") ?? "").trim();

  const isEmail = q.includes("@") && q.includes(".");
  const isAddress = RE_ADDRESS.test(q);
  const isUuid = RE_UUID.test(q);
  const isLastFour = RE_LASTFOUR.test(q);
  const isHandle = !isEmail && RE_HANDLE.test(q);
  const handle = q.replace(/^@/, "").toLowerCase();
  const maybeProviderId = q.length >= 4 && !isAddress && !isUuid;

  // Field-equality queries (each enabled only when the input looks right).
  const usersByEmail = useFirestoreQuery<Record<string, unknown>>(
    COLLECTIONS.users,
    { filters: [["primaryEmail", "==", q]] },
    isEmail,
  );
  const usersByHandle = useFirestoreQuery<Record<string, unknown>>(
    COLLECTIONS.users,
    { filters: [["handle", "==", handle]] },
    isHandle,
  );
  const walletsByMain = useFirestoreQuery<Record<string, unknown>>(
    COLLECTIONS.wallets,
    { filters: [["mainAddress", "==", q]] },
    isAddress,
  );
  const walletsByReserve = useFirestoreQuery<Record<string, unknown>>(
    COLLECTIONS.wallets,
    { filters: [["reserveAddress", "==", q]] },
    isAddress,
  );
  const cardsByLastFour = useFirestoreQuery<Record<string, unknown>>(
    COLLECTIONS.cards,
    { filters: [["lastFour", "==", q]], limit: 50 },
    isLastFour,
  );
  const paymentsByProvider = useFirestoreQuery<Record<string, unknown>>(
    COLLECTIONS.payments,
    { filters: [["providerAuthorizationId", "==", q]] },
    maybeProviderId,
  );
  const refundsByProvider = useFirestoreQuery<Record<string, unknown>>(
    COLLECTIONS.cardRefunds,
    { filters: [["foreignId", "==", q]], limit: 50 },
    maybeProviderId || isUuid,
  );

  // Direct doc-id lookups (uuid).
  const userById = useFirestoreDoc<Record<string, unknown>>(COLLECTIONS.users, isUuid ? q : undefined);
  const walletById = useFirestoreDoc<Record<string, unknown>>(COLLECTIONS.wallets, isUuid ? q : undefined);
  const paymentById = useFirestoreDoc<Record<string, unknown>>(COLLECTIONS.payments, isUuid ? q : undefined);
  const refundById = useFirestoreDoc<Record<string, unknown>>(COLLECTIONS.cardRefunds, isUuid ? q : undefined);
  const cardById = useFirestoreDoc<Record<string, unknown>>(COLLECTIONS.cards, isUuid ? q : undefined);
  const sessionKeyById = useFirestoreDoc<Record<string, unknown>>(COLLECTIONS.sessionKeys, isUuid ? q : undefined);

  const groups = useMemo<Group[]>(() => {
    const users = dedupe([
      ...(userById.data ? [userById.data] : []),
      ...usersByEmail.data,
      ...usersByHandle.data,
    ]);
    const wallets = dedupe([
      ...(walletById.data ? [walletById.data] : []),
      ...walletsByMain.data,
      ...walletsByReserve.data,
    ]);
    const cards = dedupe([...(cardById.data ? [cardById.data] : []), ...cardsByLastFour.data]);
    const payments = dedupe([...(paymentById.data ? [paymentById.data] : []), ...paymentsByProvider.data]);
    const refunds = dedupe([...(refundById.data ? [refundById.data] : []), ...refundsByProvider.data]);
    const sessionKeys = dedupe(sessionKeyById.data ? [sessionKeyById.data] : []);

    return [
      { entity: "user" as const, rows: users },
      { entity: "wallet" as const, rows: wallets },
      { entity: "card" as const, rows: cards },
      { entity: "payment" as const, rows: payments },
      { entity: "cardRefund" as const, rows: refunds },
      { entity: "sessionKey" as const, rows: sessionKeys },
    ].filter((g) => g.rows.length > 0);
  }, [
    userById.data,
    usersByEmail.data,
    usersByHandle.data,
    walletById.data,
    walletsByMain.data,
    walletsByReserve.data,
    cardById.data,
    cardsByLastFour.data,
    paymentById.data,
    refundById.data,
    refundsByProvider.data,
    paymentsByProvider.data,
    sessionKeyById.data,
  ]);

  const total = groups.reduce((n, g) => n + g.rows.length, 0);

  return (
    <>
      <PageHeader
        title="Search"
        subtitle={
          <Group gap={6}>
            <Text size="sm" c="dimmed">
              {q ? `${total} result${total === 1 ? "" : "s"} for` : "Enter a query"}
            </Text>
            {q ? <Code>{q}</Code> : null}
          </Group>
        }
      />
      <Stack gap="lg">
        {!q ? (
          <EmptyState
            title="Search the system"
            description="By user email · @handle · user/wallet id (uuid) · 0x address · card last-four · payment/refund id · Rain transaction reference."
          />
        ) : total === 0 ? (
          <EmptyState title="No matches" description={`Nothing found for "${q}".`} />
        ) : (
          groups.map((g) => (
            <Card key={g.entity} withBorder radius="md" padding="md">
              <Group justify="space-between" mb="xs">
                <Text fw={600}>{entityLabel(g.entity)}s</Text>
                <Text size="sm" c="dimmed">
                  {g.rows.length}
                </Text>
              </Group>
              <DataTable
                data={g.rows}
                columns={columnsFor(g.entity)}
                searchable={false}
                onRowClick={
                  entityHasDetail(g.entity)
                    ? (row) => navigate(entityPath(g.entity, (row as WithDocId<unknown>)._docId))
                    : undefined
                }
              />
            </Card>
          ))
        )}
      </Stack>
    </>
  );
}
